import React, { useState, useEffect, useCallback } from "react";
import {
  Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, Box, Grid, TextField, TablePagination, useMediaQuery, useTheme,
  Card, CardContent, Button, CardActions, CircularProgress, IconButton, Tooltip,
  Divider, InputAdornment, Stack, alpha, Chip,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getMiBandejaPaginada } from "../../componentes/http/actions/BandejaSuscriptorAction";
import DetalleNormativaSuscriptorModal from "../../componentes/modal/Normativa/DetalleNormativaSuscriptorModal";

const formatFecha = (value) =>
  value ? new Date(value).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";

const BandejaSuscriptor = () => {
  const token = localStorage.getItem("token");

  const [documentos, setDocumentos] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [searchTerm, setSearchTerm] = useState("");
  const [regulador, setRegulador] = useState("");
  const [tipoDocumento, setTipoDocumento] = useState("");
  const [rubro, setRubro] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [openDetalle, setOpenDetalle] = useState(false);
  const [normaIdSel, setNormaIdSel] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchBandeja = useCallback(() => {
    setIsLoading(true);
    const filtros = { searchTerm, regulador, tipoDocumento, rubro };
    getMiBandejaPaginada(token, page + 1, rowsPerPage, filtros)
      .then((response) => {
        if (response?.items && Array.isArray(response.items)) {
          setDocumentos(response.items);
          setTotalCount(response.totalCount || 0);
        } else {
          setDocumentos([]);
          setTotalCount(0);
        }
      })
      .catch((error) => {
        console.error("Error al cargar mi bandeja:", error);
        setDocumentos([]);
        setTotalCount(0);
      })
      .finally(() => {
        setIsLoading(false);
        setInitialLoad(true);
      });
  }, [page, rowsPerPage, searchTerm, regulador, tipoDocumento, rubro, token]);

  useEffect(() => { fetchBandeja(); }, [fetchBandeja]);

  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  // Ahora guardamos solo el id (NormaScraperId); el modal carga el detalle.
  const handleOpenDetalle = (doc) => { setNormaIdSel(doc.id); setOpenDetalle(true); };
  const handleCloseDetalle = () => { setOpenDetalle(false); setNormaIdSel(null); };

  const handleResetFilters = () => {
    setSearchTerm(""); setRegulador(""); setTipoDocumento(""); setRubro("");
    setStartDate(null); setEndDate(null); setPage(0);
  };

  const columns = [
    { id: "nombre", label: "Nombre", minWidth: 240 },
    { id: "regulador", label: "Regulador", minWidth: 140 },
    { id: "pais", label: "País", minWidth: 110 },
    { id: "tipoDocumento", label: "Tipo", minWidth: 120 },
    { id: "fecha", label: "Publicación", minWidth: 110, format: formatFecha },
    { id: "acciones", label: "Acciones", minWidth: 110, align: "right" },
  ];

  const renderMobileCard = (doc) => (
    <Card key={doc.id} elevation={1} sx={{ mb: 1.5 }}>
      <CardContent sx={{ pb: 1 }}>
        <Typography variant="subtitle2" sx={{ wordBreak: "break-word", mb: 1 }}>{doc.nombre}</Typography>
        <Divider sx={{ my: 1 }} />
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Regulador</Typography>
            <Typography variant="body2">{doc.regulador}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">País</Typography>
            <Typography variant="body2">{doc.pais}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Tipo</Typography>
            <Typography variant="body2">{doc.tipoDocumento}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Publicación</Typography>
            <Typography variant="body2">{formatFecha(doc.fecha)}</Typography>
          </Grid>
          {doc.rubros?.length > 0 && (
            <Grid item xs={12}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {doc.rubros.map((r) => <Chip key={r.id} label={r.nombre} size="small" variant="outlined" />)}
              </Stack>
            </Grid>
          )}
        </Grid>
      </CardContent>
      <CardActions sx={{ px: 2, pb: 2, flexWrap: "wrap", gap: 1 }}>
        <Button variant="contained" size="small" onClick={() => handleOpenDetalle(doc)} startIcon={<VisibilityOutlinedIcon />}>
          Detalle
        </Button>
        {doc.linkSitioWeb && (
          <Button variant="outlined" size="small" component="a" href={doc.linkSitioWeb} target="_blank" rel="noopener noreferrer" startIcon={<DescriptionOutlinedIcon />}>
            Documento
          </Button>
        )}
      </CardActions>
    </Card>
  );

  return (
    <Box sx={{ pt: { xs: 2.5, sm: 1.5 }, pb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5">Mis normativas</Typography>
        <Typography variant="body2" color="text.secondary">
          Estas son las normas que coinciden con tu suscripción.
        </Typography>
      </Box>

      {/* Filtros */}
      <Paper elevation={1} sx={{ p: { xs: 2, sm: 2.5 }, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, textTransform: "uppercase", letterSpacing: "0.04em", fontSize: "0.72rem" }}>
          Filtros de búsqueda
        </Typography>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth label="Buscar por nombre" value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" color="disabled" /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField fullWidth label="Regulador" value={regulador}
              onChange={(e) => { setRegulador(e.target.value); setPage(0); }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField fullWidth label="Tipo de documento" value={tipoDocumento}
              onChange={(e) => { setTipoDocumento(e.target.value); setPage(0); }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField fullWidth label="Rubro" value={rubro}
              onChange={(e) => { setRubro(e.target.value); setPage(0); }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker sx={{ width: "100%" }} format="dd/MM/yyyy" label="Fecha desde"
                value={startDate} onChange={(v) => { setStartDate(v); setPage(0); }}
                slotProps={{ textField: { size: "small" } }} />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker sx={{ width: "100%" }} format="dd/MM/yyyy" label="Fecha hasta"
                value={endDate} onChange={(v) => { setEndDate(v); setPage(0); }}
                slotProps={{ textField: { size: "small" } }} />
            </LocalizationProvider>
          </Grid>
        </Grid>
        <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
          <Button variant="outlined" onClick={handleResetFilters}>Limpiar filtros</Button>
        </Stack>
      </Paper>

      {/* Resultados */}
      {(!initialLoad || isLoading) ? (
        <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>
      ) : documentos.length > 0 ? (
        <>
          {isMobile ? (
            <Box>{documentos.map(renderMobileCard)}</Box>
          ) : (
            <TableContainer component={Paper} elevation={1} sx={{ overflow: "auto" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.id} align={col.align || "left"} sx={{ minWidth: col.minWidth, py: 1.25, px: 2, whiteSpace: "nowrap" }}>
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documentos.map((doc) => (
                    <TableRow key={doc.id} hover sx={{ "&:hover": { backgroundColor: (t) => alpha(t.palette.primary.main, 0.04) } }}>
                      {columns.map((col) => {
                        if (col.id === "acciones") {
                          return (
                            <TableCell key={col.id} align="right" sx={{ px: 1.5 }}>
                              <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                                <Tooltip title="Ver detalle">
                                  <IconButton size="small" color="primary" onClick={() => handleOpenDetalle(doc)}>
                                    <VisibilityOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {doc.linkSitioWeb && (
                                  <Tooltip title="Ver documento">
                                    <IconButton size="small" color="primary" component="a" href={doc.linkSitioWeb} target="_blank" rel="noopener noreferrer">
                                      <DescriptionOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          );
                        }
                        const value = doc[col.id];
                        return (
                          <TableCell key={col.id} align={col.align || "left"}
                            sx={{
                              py: 1.25, px: 2, maxWidth: col.id === "nombre" ? 320 : 180,
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                              fontWeight: col.id === "nombre" ? 600 : 400,
                            }}
                          >
                            {col.format ? col.format(value) : value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]} component="div" count={totalCount}
            rowsPerPage={rowsPerPage} page={page}
            onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </>
      ) : (
        <Paper sx={{ p: 5, textAlign: "center" }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Sin resultados</Typography>
          <Typography variant="body2" color="text.secondary">
            Ninguna normativa coincide con los filtros actuales. Ajusta la búsqueda o límpiala para ver todo.
          </Typography>
          <Button onClick={handleResetFilters} sx={{ mt: 2 }} variant="outlined" size="small">
            Limpiar filtros
          </Button>
        </Paper>
      )}

      {openDetalle && (
        <DetalleNormativaSuscriptorModal
          open={openDetalle}
          onClose={handleCloseDetalle}
          normaId={normaIdSel}
        />
      )}
    </Box>
  );
};

export default BandejaSuscriptor;